import React from 'react'

import { TFunction } from 'i18next'

import { useEventCapture } from '@surglogs-app/analytics'
import {
  MfaAbortTimerText,
  MfaAuthenticatorAppSetup,
} from '@surglogs-app/auth-mfa'
import { useHandler } from '@surglogs-app/hooks'
import { useAccessManagementAccount } from '@surglogs-app/network'
import { Box, Button, Divider } from '@surglogs-app/ui'

import { useInitiateMfaSetup } from '../hooks/useInitiateMfaSetup'

interface MfaAuthenticatorSetupFormProps {
  setActiveView: (view: 'mfa_step1' | 'mfa_step2') => void
  onClose: () => void
  t: TFunction<any>
  context: 'account' | 'settings'
  transactionId: string
  isLastMinute: boolean
  timeLeft: number | null
}

export const MfaAuthenticatorSetupForm = ({
  transactionId,
  context,
  isLastMinute,
  timeLeft,
  setActiveView,
  onClose,
  t,
}: MfaAuthenticatorSetupFormProps) => {
  const { captureEvent } = useEventCapture()

  const { data: user, isLoading } = useAccessManagementAccount({})

  const { secretCode, isPending, initiateMFASetup } = useInitiateMfaSetup({
    transactionId,
    context,
  })

  const goToNextStep = useHandler(() => {
    captureEvent('mfa_setup_next_step_clicked', { context })
    setActiveView('mfa_step2')
  })

  return (
    <>
      <Box {...{ px: 'xl', py: 'md', width: '100%' }}>
        <MfaAuthenticatorAppSetup
          {...{
            email: user?.email ?? '',
            code: secretCode,
            isPending: isLoading || isPending,
            initiateMFASetup,
            showTitle: false,
            showDivider: false,
            t,
          }}
        />
      </Box>

      <MfaAbortTimerText {...{ mode: 'setup', isLastMinute, timeLeft, t }} />

      <Divider {...{ my: 'abs.30', mx: 'abs.0' }} />

      <Box {...{ px: 'xl', pb: 'xl', width: '100%' }}>
        <Button {...{ onClick: goToNextStep, disabled: !secretCode }}>
          {t('mfaSetup.step1.next')}
        </Button>
        <Button {...{ variant: 'secondary', ml: 'md', onClick: onClose }}>
          {t('mfaSetup.step1.cancel')}
        </Button>
      </Box>
    </>
  )
}
